import React, { Suspense, lazy } from "react";
import {
    Routes,
    Route,
    Navigate,
    useLocation,
    useNavigate,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import ProtectedRoute from "../components/ProtectedRoute";
import MobileBottomBar from "../components/MobileBottomBar";
import { AuthProvider, useAuth } from "../context/AuthContext";
import { NotificationProvider } from "../context/NotificationContext";
import Topbar from "../components/TopBar";
import ScrollToTop from "../components/ScrollToTop";
import OnboardingWizard from "../components/onboarding/OnboardingWizard";
import { GoogleMapsProvider } from "../context/GoogleMapsContext";
import { FeedProvider } from "../context/FeedContext";
import { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

import { useGuestDraftSync } from "../hooks/useGuestDraftSync";
import ReportLostPetDialog from "../features/landing/components/ReportLostPetDialog";
import ReportFoundPetDialog from "../features/landing/components/ReportFoundPetDialog";

import "yet-another-react-lightbox/styles.css";

import ProfileSkeleton from "../features/account/components/ProfileSkeleton";

// Lazy Loaded Pages
const Dashboard = lazy(() => import("../features/dashboard/DashboardPage"));
const ListPetsPage = lazy(() => import("../features/pets/pages/ListPetsPage"));
const ShowPetPage = lazy(() => import("../features/pets/pages/ShowPetPage"));
const PetInfoPage = lazy(() => import("../features/pets/pages/PetInfoPage"));
const LoginPage = lazy(() => import("../features/authtentication/LoginPage"));
const RegisterPage = lazy(() => import("../features/authtentication/RegisterPage"));
const VerifyEmailPage = lazy(() => import("../features/authtentication/VerifyEmailPage"));
const ForgotPasswordPage = lazy(() => import("../features/authtentication/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("../features/authtentication/ResetPasswordPage"));
const UserProfilePage = lazy(() => import("../features/account/pages/Profile"));
const EditProfilePage = lazy(() => import("../features/account/pages/EditProfile"));
const PetReportsMapPage = lazy(() => import("../features/pets/pages/PetReportsMapPage"));
const ReportDetailPage = lazy(() => import("../features/pets/pages/ReportDetailPage"));
const MunicipalitiesIndexPage = lazy(() => import("../features/pets/pages/MunicipalitiesIndexPage"));
const BlogPage = lazy(() => import("../features/blog/pages/BlogPage"));
const BlogDetailPage = lazy(() => import("../features/blog/pages/BlogDetailPage"));
const PostPage = lazy(() => import("../features/posts/pages/PostPage"));

const LandingPage = lazy(() => import("../features/landing/pages/LandingPage"));
const PosterCreatorPage = lazy(() => import("../features/pets/pages/PosterCreatorPage"));
const NotificationsPage = lazy(() => import("../features/notifications/pages/NotificationsPage"));


function LoadingFallback() {
    return (
        <div className="flex items-center justify-center min-h-[60vh]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
    );
}


const ROUTE_BY_KEY = {
    landing: "/",
    dashboard: "/dashboard",
    pets: "/pets",
    blog: "/blog",
    "ai-health-check": "/ai-health-check",
    "reports": "/reports",
    profile: "/profile",
    poster: "/poster-creator",
    notifications: "/notifications",
};

const KEY_BY_PATH = {
    "/": "landing",
    "/dashboard": "dashboard",
    "/pets": "pets",
    "/blog": "blog",
    "/ai-health-check": "ai-health-check",
    "/profile": "profile",
    "/login": null,
    "/register": null,
    "/verify-email": null,
    "/forgot-password": null,
    "/reset-password": null,
    "/reports": "reports",
    "/poster-creator": "poster",
    "/notifications": "notifications",
};

function Placeholder({ title }) {
    return (
        <div className="max-w-4xl mx-auto">
            <header className="mb-8">
                <h1 className="text-3xl font-bold">{title}</h1>
            </header>
            <p className="text-subtle-light dark:text-subtle-dark">
                Placeholder. Reemplaza este contenido cuando conectes la API.
            </p>
        </div>
    );
}

function AppShell() {
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useAuth();

    const { syncedLostReport, syncedFoundReport, clearSyncedReport } = useGuestDraftSync(); // Sync guest drafts on login


    // Fix activeKey logic for blog and profile detail pages
    let activeKey = KEY_BY_PATH[location.pathname];
    if (!activeKey) {
        if (location.pathname.startsWith("/profile") || location.pathname.startsWith("/users/")) {
            activeKey = "profile";
        } else if (location.pathname.startsWith("/blog")) {
            activeKey = "blog";
        } else if (location.pathname.startsWith("/reports")) {
            activeKey = "reports";
        } else if (location.pathname.startsWith("/poster-creator")) {
            activeKey = "poster";
        } else if (location.pathname.startsWith("/notifications")) {
            activeKey = "notifications";
        } else {
            activeKey = "dashboard";
        }
    }

    const handleSelect = (key) => {
        const targetPath = ROUTE_BY_KEY[key] || "/dashboard";

        // Only scroll to top if we are already at the exact target path
        if (key === activeKey && location.pathname === targetPath) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            navigate(targetPath);
        }
    };

    const isLandingOrLogin = ["/", "/login", "/register", "/verify-email", "/forgot-password", "/reset-password"].includes(location.pathname);
    const isStandaloneTool = ["/poster-creator"].includes(location.pathname);
    const hideSystemNav = isLandingOrLogin || isStandaloneTool;

    const { pathname: path } = location;
    const isPetDetail = path.startsWith("/pets/") && path.split("/").length > 2;
    const isUserProfileView =
        path === "/profile" || path.startsWith("/users/");
    const isMainFullBleedMobile = isPetDetail || isUserProfileView || path.startsWith("/blog") || path === "/notifications";

    return (
        <div className="w-full overflow-x-hidden md:overflow-x-visible min-h-screen bg-white dark:bg-black font-display text-foreground-light dark:text-foreground-dark">
            <ScrollToTop />
            <div className="flex flex-col min-h-screen">
                {/* Topbar solo desktop (no en landing ni login ni standalone features) */}
                {!hideSystemNav && (
                    <Topbar
                        active={activeKey}
                        onSelect={handleSelect}
                        user={user}
                    />
                )}

                <main
                    className={
                        !hideSystemNav
                            ? `flex-1 md:pt-20 pb-28 md:pb-8`
                            : "flex-1"
                    }
                >
                    <div
                        className={
                            !hideSystemNav
                                ? (path.startsWith("/reports"))
                                    ? "w-full max-w-6xl mx-auto px-0 lg:px-0"
                                    : isMainFullBleedMobile
                                        ? "w-full max-w-6xl mx-auto px-0 lg:px-2"
                                        : "w-full max-w-6xl mx-auto px-4 lg:px-0"
                                : ""
                        }
                    >
                        <Suspense fallback={<LoadingFallback />}>
                            <Routes>
                                <Route
                                    path="/"
                                    element={user ? <Navigate to="/dashboard" replace /> : <LandingPage />}
                                />

                                <Route path="/login" element={<LoginPage />} />
                                <Route path="/register" element={<RegisterPage />} />
                                <Route path="/verify-email" element={<VerifyEmailPage />} />
                                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                                <Route path="/reset-password" element={<ResetPasswordPage />} />


                                <Route
                                    path="/dashboard"
                                    element={
                                        <ProtectedRoute>
                                            <Dashboard />
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/pets"
                                    element={
                                        <ProtectedRoute>
                                            <ListPetsPage title="Pets" />
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/pets/:id"
                                    element={
                                        <ProtectedRoute>
                                            <ShowPetPage />
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/pets/:id/info"
                                    element={
                                        <PetInfoPage />
                                    }
                                />

                                <Route
                                    path="/reports"
                                    element={
                                        <PetReportsMapPage />
                                    }
                                />





                                {/* Blog Routes */}

                                <Route path="/blog" element={<BlogPage />}>
                                    <Route path=":slugOrId" element={<BlogDetailPage />} />
                                </Route>

                                {/* Poster Creator — standalone public tool */}
                                <Route path="/poster-creator" element={<PosterCreatorPage />} />

                                {/* Post Detail Route */}
                                <Route
                                    path="/posts/:slugOrId"
                                    element={<PostPage />}
                                />


                                <Route
                                    path="/notifications"
                                    element={
                                        <ProtectedRoute>
                                            <NotificationsPage />
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/ai-health-check"
                                    element={
                                        <ProtectedRoute>
                                            <Placeholder title="AI Health Check" />
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/profile"
                                    element={
                                        <ProtectedRoute>
                                            <Suspense fallback={<ProfileSkeleton />}>
                                                <UserProfilePage />
                                            </Suspense>
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/profile/edit"
                                    element={
                                        <ProtectedRoute>
                                            <EditProfilePage />
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/users/:id"
                                    element={
                                        <ProtectedRoute>
                                            <Suspense fallback={<ProfileSkeleton />}>
                                                <UserProfilePage />
                                            </Suspense>
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="*"
                                    element={<Placeholder title="Not Found" />}
                                />

                                <Route path="/municipalities" element={<MunicipalitiesIndexPage />} />

                                <Route
                                    path="/reports/:countryCode/:municipalitySlug/:reportType?"
                                    element={<PetReportsMapPage />}
                                />
                                <Route
                                    path="/reports/:countryCode/:municipalitySlug/:reportType/:slug"
                                    element={<ReportDetailPage />}
                                />
                            </Routes>
                        </Suspense>
                    </div>
                </main>
            </div>

            {/* Bottom bar solo móvil */}
            {!hideSystemNav && (
                <MobileBottomBar
                    active={activeKey}
                    onSelect={handleSelect}
                    user={user}
                />
            )}
            <OnboardingWizard />

            {/* Global Dialog for completing Lost Pet Report from Guest Draft */}
            <ReportLostPetDialog
                open={!!syncedLostReport}
                onClose={clearSyncedReport}
                existingReport={syncedLostReport}
                initialStep={6}
            />

            <ReportFoundPetDialog
                open={!!syncedFoundReport}
                onClose={clearSyncedReport}
                existingReport={syncedFoundReport}
                initialStep={6}
            />

        </div>
    );

}


export default function App() {
    return (
        <HelmetProvider>
            <AuthProvider>
                <NotificationProvider>
                    <GoogleMapsProvider>
                        <FeedProvider>
                            <SkeletonTheme baseColor="var(--skeleton-base)" highlightColor="var(--skeleton-highlight)">
                                <AppShell />
                            </SkeletonTheme>
                        </FeedProvider>
                    </GoogleMapsProvider>
                </NotificationProvider>
            </AuthProvider>
        </HelmetProvider>
    );
}